import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  ProjectPDFGeneralDataOption,
  ProjectPDFInstallationReferencesOption,
  ProjectPDFPriceOption,
  ProjectPDFRoomDataOption
} from "shared/types/PDFExport"
import { SelectOption } from "shared/types/Select"

import {
  ProjectPDFOverlayFormData,
  ProjectPDFOverlayProps
} from "../ProjectPDFOverlay"
import { ProjectPDFOverlayViewModel } from "../ProjectPDFOverlayView"

export const defaultProjectPDFOverlayFormData: ProjectPDFOverlayFormData = {
  position: [
    ProjectPDFGeneralDataOption.REMARKS,
    ProjectPDFGeneralDataOption.DIMENSIONING_DATA,
    ProjectPDFGeneralDataOption.PRODUCT_DESCRIPTION
    // ProjectPDFGeneralDataOption.DELIVERY_TIME
  ],
  room: [
    ProjectPDFRoomDataOption.DIMENSIONS_DATA,
    ProjectPDFRoomDataOption.AIR_DATA,
    ProjectPDFRoomDataOption.REMARK
  ],
  price: ProjectPDFPriceOption.PRICES_AND_GROUPS,
  installationReference:
    ProjectPDFInstallationReferencesOption.NO_INSTALLATION_REFERENCES
}

export function useProjectPDFOverlayViewModel({
  onSubmit,
  ...rest
}: ProjectPDFOverlayProps): ProjectPDFOverlayViewModel {
  const { t } = useTranslation()

  const form = useForm({ defaultValues: defaultProjectPDFOverlayFormData })

  const handleSubmit = useMemo(() => form.handleSubmit(onSubmit), [onSubmit])

  const positionDataOptions: SelectOption<ProjectPDFGeneralDataOption>[] =
    useMemo(
      () => [
        {
          label: t("ProjectExport.PositionDataOptions.Description"),
          value: ProjectPDFGeneralDataOption.PRODUCT_DESCRIPTION
        },
        // {
        //   label: t("ProjectExport.PositionDataOptions.DeliveryTime"),
        //   value: ProjectPDFGeneralDataOption.DELIVERY_TIME
        // },
        {
          label: t("ProjectExport.PositionDataOptions.DimensioningData"),
          value: ProjectPDFGeneralDataOption.DIMENSIONING_DATA
        },
        {
          label: t("ProjectExport.PositionDataOptions.Remark"),
          value: ProjectPDFGeneralDataOption.REMARKS
        }
      ],
      [t]
    )

  const roomDataOptions: SelectOption<ProjectPDFRoomDataOption>[] = useMemo(
    () => [
      {
        label: t("ProjectExport.RoomDataOptions.DimensionsData"),
        value: ProjectPDFRoomDataOption.DIMENSIONS_DATA
      },
      {
        label: t("ProjectExport.RoomDataOptions.AirData"),
        value: ProjectPDFRoomDataOption.AIR_DATA
      },
      {
        label: t("ProjectExport.RoomDataOptions.Remark"),
        value: ProjectPDFRoomDataOption.REMARK
      }
    ],
    [t]
  )

  const priceDataOptions: SelectOption<ProjectPDFPriceOption>[] = useMemo(
    () => [
      {
        label: t("ProjectExport.PriceDataOptions.Exclude"),
        value: ProjectPDFPriceOption.NO_PRICES
      },
      {
        label: t("ProjectExport.PriceDataOptions.PricesOnly"),
        value: ProjectPDFPriceOption.PRICES_ONLY
      },
      {
        label: t("ProjectExport.PriceDataOptions.PricesAndPriceGroups"),
        value: ProjectPDFPriceOption.PRICES_AND_GROUPS
      }
    ],
    [t]
  )

  const installationReferenceOptions: SelectOption<ProjectPDFInstallationReferencesOption>[] =
    useMemo(
      () => [
        {
          label: t("ProjectExport.InstallationReferenceOptions.Exclude"),
          value:
            ProjectPDFInstallationReferencesOption.NO_INSTALLATION_REFERENCES
        },
        {
          label: t("ProjectExport.InstallationReferenceOptions.Images"),
          value: ProjectPDFInstallationReferencesOption.IMAGES
        },
        {
          label: t("ProjectExport.InstallationReferenceOptions.QRCode"),
          value: ProjectPDFInstallationReferencesOption.QR_CODE
        },
        {
          label: t("ProjectExport.InstallationReferenceOptions.QRCodeAndImage"),
          value: ProjectPDFInstallationReferencesOption.QR_CODE_AND_IMAGE
        }
      ],
      [t]
    )

  return {
    ...rest,
    form,
    positionDataOptions,
    priceDataOptions,
    roomDataOptions,
    installationReferenceOptions,
    onSubmit: handleSubmit
  }
}
